/**
 * InOut — calendar dates as whole days.
 *
 * A record is kept in dates (`YYYY-MM-DD`), not instants: a person is in or out
 * of the country on a DAY. The engine counts on day numbers (days since
 * 1970-01-01, UTC — no time zone, no daylight saving) and measures windows on
 * the calendar (`counting.windowArithmetic: CALENDAR`): "a period of three
 * years" ends on the same calendar date three years on.
 */

/** A calendar date, `YYYY-MM-DD`. */
export type IsoDate = string;

const MS_PER_DAY = 86_400_000;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** The day number of a date. */
export function dayOf(date: IsoDate): number {
  const match = ISO_DATE.exec(date);
  if (!match) throw new Error(`Not a calendar date (YYYY-MM-DD): ${date}`);
  const [, y, m, d] = match;
  const ms = Date.UTC(Number(y), Number(m) - 1, Number(d));
  if (new Date(ms).getUTCDate() !== Number(d)) throw new Error(`No such date: ${date}`);
  return ms / MS_PER_DAY;
}

/** The date of a day number. */
export function dateOf(day: number): IsoDate {
  return new Date(day * MS_PER_DAY).toISOString().slice(0, 10);
}

/**
 * The same calendar date `months` later (or earlier), clamped to the month's
 * last day: 31 Jan + 1 month = 28/29 Feb.
 */
export function addMonths(day: number, months: number): number {
  const date = new Date(day * MS_PER_DAY);
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() + months;
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return Date.UTC(year, month, Math.min(date.getUTCDate(), lastDay)) / MS_PER_DAY;
}

/** The first and last day of the calendar year a day falls in. */
export function calendarYearOf(day: number): { start: number; end: number } {
  const year = new Date(day * MS_PER_DAY).getUTCFullYear();
  return {
    start: Date.UTC(year, 0, 1) / MS_PER_DAY,
    end: Date.UTC(year, 11, 31) / MS_PER_DAY,
  };
}
